import {
  Upload,
  User,
  AtSign,
  Loader2,
  UserCircle,
  BookOpen,
  Briefcase,
  GraduationCap,
  LogOut,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@packages/store/hooks";
import { useState, useEffect } from "react";
import profileImage from "@packages/assets/profilebg1.jpg";
import { editTeacher } from "@packages/api/madlenAPI";
import { getCurrentTeacher } from "@packages/store/userSlice";
import { useToast } from "@packages/contexts/ToastContext";
import { useTranslation } from "react-i18next";
import { trackEvent } from "@packages/hooks/useAnalytics";
import EdlinkTeacherClassesView from "@features/tools/view/edlink-teacher-classes-view";
import { Button } from "@core/button";
import { Text } from "@core/text";
import { useSearchParams } from "react-router-dom";
import MultiSelectSearch from "@core/multi-select";
import {
  gradeOptions,
  roleOptions,
  schoolOptions,
} from "@features/profiles/constant/school";

const InputField = ({
  label,
  type = "text",
  icon: Icon,
  value,
  placeholder,
  disabled = false,
  onChange,
}: {
  label: string;
  type?: string;
  icon: any;
  value?: string;
  placeholder: string;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
  <div className="relative">
    <label
      className={`block text-sm font-medium mb-1 ${
        disabled
          ? "text-gray-400 dark:text-gray-500"
          : "text-gray-700 dark:text-gray-300"
      }`}
    >
      {label}
    </label>
    <div className="relative group">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Icon
          className={`h-5 w-5 transition-colors ${
            disabled
              ? "text-gray-300 dark:text-gray-600"
              : "text-gray-400 dark:text-gray-500 group-focus-within:text-primary-500 dark:group-focus-within:text-primary-400"
          }`}
        />
      </div>
      <input
        type={type}
        className={`block w-full pl-10 pr-4 py-2.5 text-sm rounded-lg border shadow-sm transition-all duration-200
                   ${
                     disabled
                       ? "bg-gray-50 dark:bg-dark-600 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                       : "border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-600 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 dark:focus:border-primary-400 focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-primary-400/20 hover:border-primary-500 dark:hover:border-primary-400"
                   }
                   placeholder:text-gray-400 dark:placeholder:text-gray-500`}
        value={value || ""}
        placeholder={placeholder}
        disabled={disabled}
        onChange={onChange}
      />
      <div
        className={`absolute inset-0 rounded-lg border border-transparent pointer-events-none transition-all duration-200
                    ${
                      disabled
                        ? ""
                        : "group-hover:border-primary-500 dark:group-hover:border-primary-400 group-focus-within:border-primary-500 dark:group-focus-within:border-primary-400 group-focus-within:ring-2 group-focus-within:ring-primary-500/20 dark:group-focus-within:ring-primary-400/20"
                    }`}
      />
    </div>
  </div>
);

const MyProfileViewModelInside = ({ onLogout }: { onLogout?: () => void }) => {
  const dispatch = useAppDispatch();
  const { addToast } = useToast();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const currentAvatar = useAppSelector((state) => state.user.avatar);
  const currentName = useAppSelector((state) => state.user.teacher?.name);
  const currentSurname = useAppSelector((state) => state.user.teacher?.surname);
  const currentEmail = useAppSelector((state) => state.user.teacher?.email);
  const teacherId = useAppSelector((state) => state.user.teacher?.id);
  const currentSchool = useAppSelector((state) => state.user.teacher?.school);
  const currentRole = useAppSelector((state) => state.user.teacher?.role);
  const currentGrades = useAppSelector((state) => state.user.teacher?.grade);
  const edlinkPersonId = useAppSelector(
    (state) => state.user.teacher?.edlinkPersonId,
  );
  const edlinkActivated = useAppSelector(
    (state) => state.user.teacher?.hasActiveEdlinkToken,
  );
  const isEdlinkUser = !!edlinkPersonId;

  const defaultSchool = currentSchool
    ? [
        {
          value: currentSchool,
          label: currentSchool,
        },
      ]
    : [];

  const defaultRole = currentRole
    ? [
        {
          value: currentRole,
          label: currentRole,
        },
      ]
    : [];

  const defaultGrades = currentGrades
    ? currentGrades.map((grade) => ({
        value: grade,
        label: grade,
      }))
    : [];

  const [avatarPreview, setAvatarPreview] = useState<string | null>(
    currentAvatar,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState(currentName);
  const [surname, setSurname] = useState(currentSurname);
  const [school, setSchool] =
    useState<Array<{ value: string; label: string }>>(defaultSchool);
  const [role, setRole] =
    useState<Array<{ value: string; label: string }>>(defaultRole);
  const [grades, setGrades] =
    useState<Array<{ value: string; label: string }>>(defaultGrades);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setName(currentName);
    setSurname(currentSurname);
  }, [currentName, currentSurname]);

  useEffect(() => {
    const callback = searchParams.get("callback");
    if (callback === "true" && edlinkActivated) {
      setIsModalOpen(true);
    }
  }, [searchParams, edlinkActivated]);

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const { trackEvent: track } = trackEvent();
  const handleClickSave = () => {
    track(
      {
        category: "ContentGeneration",
        action: "profile.save",
        label: "Profile save click",
        nonInteraction: false,
      },
      {
        location: "/profile/save",
        element: "save-button",
      },
    );
    handleSaveChanges();
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);

      await editTeacher({
        name,
        surname,
        school: school.length > 0 ? school[0].label : undefined,
        role: role.length > 0 ? role[0].label : undefined,
        grade: grades.map((grade) => grade.label),
      });
      dispatch(getCurrentTeacher());

      addToast({
        title: t("profile.changesSaved"),
        variant: "success",
        duration: 3000,
      });
    } catch (error: any) {
      setIsSaving(false);
      if (
        error.response?.data.message ===
        "Content contains inappropriate language"
      ) {
        setName(currentName);
        setSurname(currentSurname);
        addToast({
          title: t("common.inappropriateContent.title"),
          description: t("common.inappropriateContent.description"),
          variant: "error",
          duration: 3000,
        });
      } else {
        console.error("Failed to save changes:", error);
        addToast({
          title: t("profile.saveFailed"),
          variant: "error",
          duration: 3000,
        });
      }
    } finally {
      setIsSaving(false);
      dispatch(getCurrentTeacher());
    }
  };

  return (
    <div className="flex-1 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white dark:bg-dark-500 shadow-sm">
          <div className=" overflow-hidden">
            <div className="relative h-32">
              <img
                src={profileImage}
                alt={t("profile.coverImage")}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="px-8 -mt-16">
            <div className="relative inline-block">
              <div className="w-28 h-28 rounded-full border-4 border-white dark:border-white bg-white dark:bg-dark-600 shadow-md overflow-hidden">
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt={t("profile.profilePicture")}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Hide the image and show fallback if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className="w-full h-full bg-gray-100 dark:bg-dark-500 flex items-center justify-center"
                  style={{
                    display: avatarPreview ? "none" : "flex",
                  }}
                >
                  <Text variant="h1" status="secondary" size="4xl">
                    {name?.[0]}
                  </Text>
                </div>
              </div>
              <label className="hidden absolute bottom-0 right-0 bg-white dark:bg-dark-600 rounded-full p-2 shadow-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-500 transition-colors border border-gray-300 dark:border-gray-600">
                <Upload className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                <input
                  type="file"
                  className="hidden"
                  onChange={handleAvatarChange}
                  accept="image/*"
                  aria-label={t("profile.uploadPicture")}
                />
              </label>
            </div>
          </div>

          <div className="px-8 pt-4">
            {/* Profile Section */}
            <div className="pb-8">
              <div className="mb-4">
                <Text variant={"h3"}>profile.personalInformation</Text>
                <Text variant="p" status="secondary" className="mt-1">
                  {t("profile.personalInformationDesc")}
                </Text>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-6">
                  <InputField
                    label={t("profile.name")}
                    icon={User}
                    value={name}
                    placeholder={t("profile.namePlaceholder")}
                    disabled={isSaving}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <InputField
                    label={t("profile.surname")}
                    icon={UserCircle}
                    value={surname}
                    placeholder={t("profile.surnamePlaceholder")}
                    disabled={isSaving}
                    onChange={(e) => setSurname(e.target.value)}
                  />
                </div>

                <InputField
                  label={t("profile.email")}
                  type="email"
                  icon={AtSign}
                  value={currentEmail}
                  placeholder={t("profile.emailPlaceholder")}
                  disabled={true}
                />

                <div className="mt-6">
                  <Text variant={"h4"} className="mb-3">
                    {t("profile.schoolInformation")}
                  </Text>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <MultiSelectSearch
                          options={schoolOptions.sort((a, b) =>
                            a.label.localeCompare(b.label),
                          )}
                          value={school}
                          onChange={setSchool}
                          placeholder={t("profile.selectSchool")}
                          label={t("profile.school")}
                          icon={BookOpen}
                          disabled={isSaving}
                          allowCustomOptions={true}
                          multiple={false}
                        />
                      </div>

                      <div>
                        <MultiSelectSearch
                          options={roleOptions}
                          value={role}
                          onChange={setRole}
                          placeholder={t("profile.selectRole")}
                          label={t("profile.role")}
                          icon={Briefcase}
                          disabled={isSaving}
                          allowCustomOptions={true}
                          multiple={false}
                        />
                      </div>
                    </div>

                    <div>
                      <MultiSelectSearch
                        options={gradeOptions}
                        value={grades}
                        onChange={setGrades}
                        placeholder={t("profile.selectGrades")}
                        label={t("profile.grades")}
                        icon={GraduationCap}
                        disabled={isSaving}
                        multiple={true}
                      />
                    </div>
                  </div>
                </div>

                {isEdlinkUser && (
                  <div className="mt-8">
                    <Text variant={"h3"} className="mb-4">
                      profile.integrations
                    </Text>
                    <div className="bg-gray-50 dark:bg-dark-600/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <Text variant="p" status="secondary" className="mt-1">
                            {edlinkActivated
                              ? "profile.edlinkConnected"
                              : "profile.edlinkNotConnected"}
                          </Text>
                        </div>
                        {!edlinkActivated ? (
                          <Button
                            onClick={() => {
                              if (!currentEmail) return;
                              window.location.href =
                                "https://ed.link/sso/login?client_id=6d462dde-5ceb-4239-8aa1-0a2a3e36cca3&redirect_uri=https://api.madlen.io/api/edlink/oauth/callback&state=" +
                                teacherId +
                                "," +
                                window.location.origin +
                                "/profiles/me?callback=true";
                            }}
                            bgColor="bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600"
                            textColor="text-white"
                            size="md"
                            variant="outline"
                            rounded="lg"
                            className="border-transparent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:focus:ring-offset-gray-900"
                          >
                            <User className="w-4 h-4 mr-2" />
                            {t("profile.edlinkLogin")}
                          </Button>
                        ) : (
                          <Button
                            onClick={() => setIsModalOpen(true)}
                            textColor="text-white"
                            size="md"
                            rounded="lg"
                            bgColor="bg-green-600 dark:bg-green-500 hover:bg-green-700 dark:hover:bg-green-600"
                            variant="outline"
                            className="border-transparent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 dark:focus:ring-green-400 dark:focus:ring-offset-gray-900"
                          >
                            <User className="w-4 h-4 mr-2" />
                            {t("profile.importClassrooms")}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-500/50 px-8 py-4">
            <div className="flex justify-between items-center">
              {onLogout && (
                <Button
                  variant="outline"
                  size="md"
                  bgColor="bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30"
                  textColor="text-red-600 dark:text-red-400"
                  rounded="lg"
                  className="border border-red-200 dark:border-red-800/50"
                  onClick={onLogout}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t("sidebar.logout")}
                </Button>
              )}
              <div className="flex space-x-3">
                <Button
                  variant="solid"
                  size="md"
                  bgColor="bg-primary-600 dark:bg-primary-500 hover:bg-primary-700 dark:hover:bg-primary-600"
                  rounded="lg"
                  textColor="text-white"
                  disabled={isSaving}
                  onClick={handleClickSave}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("profile.saving")}
                    </>
                  ) : (
                    t("profile.saveChanges")
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-opacity-90"
              onClick={() => setIsModalOpen(false)}
            ></div>

            <div className="inline-block w-full max-w-4xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white dark:bg-dark-600 rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <Button
                  variant="solid"
                  size="icon"
                  rounded="lg"
                  textColor="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  leftIcon={
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  }
                >
                  <span className="sr-only">{t("common.close")}</span>
                </Button>
              </div>

              <div className="mt-3 sm:mt-0">
                <Text variant={"h3"} className="leading-6 mb-4">
                  profile.importClassrooms
                </Text>
                <EdlinkTeacherClassesView />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProfileViewModelInside;
